import { z } from 'zod';
import { AutoPublishSkipReasonSchema } from './auto-publish-skip-reason';
import { FeedContentCategorySlug } from './feed-category';
import { SourceChannelKind } from './source-channel';

export const SourceMessageStatus = z.enum([
  'PENDING',
  'EXTRACTED',
  'IGNORED',
  'IMPORTED',
  'REJECTED',
  'DUPLICATE',
]);
export type SourceMessageStatus = z.infer<typeof SourceMessageStatus>;

export const ImportQueueListQuery = z.object({
  status: SourceMessageStatus.default('EXTRACTED'),
  cityId: z.uuid().optional(),
  channelId: z.uuid().optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export type ImportQueueListQuery = z.infer<typeof ImportQueueListQuery>;

export const ExtractedEventFields = z.object({
  title: z.string().nullable(),
  description: z.string().nullable(),
  venueText: z.string().nullable(),
  startsAtLocalDates: z.array(z.iso.date()).default([]),
  // Открытый цикл повторения без даты окончания ("каждый понедельник"). При нём
  // оставляем ОДНО событие на ближайшую дату (см. extractEvent), иначе LLM
  // перечисляет даты на год вперёд и плодит дубль-Event'ы в ленте.
  isRecurring: z.boolean().default(false),
  startsAtLocalTime: z
    .string()
    .regex(/^\d{2}:\d{2}$/, 'HH:MM')
    .nullable(),
  endsAtLocalDate: z.iso.date().nullable(),
  endsAtLocalTime: z
    .string()
    .regex(/^\d{2}:\d{2}$/, 'HH:MM')
    .nullable(),
  price: z.number().int().nonnegative().nullable(),
  currency: z.string().length(3).nullable(),
  // Донат: свободная оплата. Может идти с номиналом (price != null — «Мин. донат
  // 200 000», комбинированный сценарий §3) либо без него (price = null,
  // «Донейшен»). Optional (как venueMapUrl) — старые фикстуры без поля → false.
  isDonation: z.boolean().optional(),
  organizerUsername: z.string().nullable(),
  categorySlugs: z.array(z.string()),
  // Google Maps-ссылка из поста (для авто-резолва координат/места при публикации).
  // Опциональна: LLM-экстрактор её пока не заполняет — undefined трактуется как null.
  venueMapUrl: z.url().nullable().optional(),
});
export type ExtractedEventFields = z.infer<typeof ExtractedEventFields>;

export const ExtractedEventDto = z.object({
  isEvent: z.boolean(),
  confidence: z.number().min(0).max(1),
  reason: z.string().nullable().optional(),
  fields: ExtractedEventFields,
  usage: z.object({
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    estimatedCostUsd: z.number().nonnegative(),
  }),
  // Какой экстрактор произвёл результат — для наблюдаемости shadow-режима.
  extractor: z.enum(['code', 'llm']).optional(),
});
export type ExtractedEventDto = z.infer<typeof ExtractedEventDto>;

export const SourceMessageDto = z.object({
  id: z.uuid(),
  channel: z.object({
    id: z.uuid(),
    slug: z.string(),
    title: z.string(),
    cityId: z.uuid(),
    kind: SourceChannelKind,
  }),
  telegramMessageId: z.number().int(),
  rawText: z.string(),
  mediaUrls: z.array(z.url()),
  videoPreviewUrl: z.url().nullable(),
  postedAt: z.iso.datetime(),
  postUrl: z.url(),
  /** Автор сообщения (темы форума); у обычных каналов null. */
  authorUsername: z.string().nullable(),
  status: SourceMessageStatus,
  extracted: ExtractedEventDto.nullable(),
  llm: z.object({
    model: z.string().nullable(),
    attempts: z.number().int().nonnegative(),
    lastError: z.string().nullable(),
    costUsd: z.number().nullable(),
  }),
  events: z.array(
    z.object({
      id: z.uuid(),
      title: z.string(),
      status: z.enum(['DRAFT', 'PENDING_MODERATION', 'PUBLISHED', 'ARCHIVED']),
      startsAt: z.iso.datetime(),
    }),
  ),
  autoPublishSkipReason: AutoPublishSkipReasonSchema.nullable(),
  duplicate: z
    .object({
      ofEventId: z.uuid().nullable(),
      ofNewsId: z.uuid().nullable(),
      similarity: z.number().nullable(),
    })
    .nullable(),
  possibleDuplicates: z
    .array(
      z.object({
        id: z.uuid(),
        title: z.string(),
        startsAt: z.iso.datetime().nullable(),
        similarity: z.number(),
      }),
    )
    .optional(),
  reviewedAt: z.iso.datetime().nullable(),
  reviewerNote: z.string().nullable(),
  createdAt: z.iso.datetime(),
});
export type SourceMessageDto = z.infer<typeof SourceMessageDto>;

export const SourceMessageListResponse = z.object({
  items: z.array(SourceMessageDto),
  nextCursor: z.string().nullable(),
});
export type SourceMessageListResponse = z.infer<typeof SourceMessageListResponse>;

// PublishFromImportInput = EventCreateInput but with `publish` defaulting to true.
// EventCreateInput is `.refine()`d — Zod 4 forbids `.omit()` on refined schemas, so we
// build a parallel object with the same shape (delegating field validation to plain
// EventCreateInput.parse first at the controller level — see admin-import-queue.service).
// Mirrors the `endsTogether` cross-field guard from EventCreateInput (event.ts) — it is
// not exported from there, so we redeclare locally with identical wording.
const endsTogether = (v: { endsAtLocalDate?: string | undefined; endsAtLocalTime?: string | undefined }) =>
  (v.endsAtLocalDate == null) === (v.endsAtLocalTime == null);

const endsTogetherMessage = 'endsAtLocal{Date,Time} must be provided together';

export const PublishFromImportInput = z
  .object({
    cityId: z.uuid(),
    title: z.string().min(1).max(200).trim(),
    description: z.string().max(5000).optional(),
    // Место не обязательно: авто-публикация и модерация проходят и без него
    // ('' из admin-формы сервис превращает в null).
    venueText: z.string().max(300).trim().optional(),
    startsAtLocalDates: z.array(z.iso.date()).min(1).max(20),
    startsAtLocalTime: z
      .string()
      .regex(/^\d{2}:\d{2}$/, 'HH:MM')
      .nullable()
      .optional(),
    endsAtLocalDate: z.iso.date().optional(),
    endsAtLocalTime: z
      .string()
      .regex(/^\d{2}:\d{2}$/, 'HH:MM')
      .optional(),
    price: z.number().int().nonnegative().nullable().optional(),
    isDonation: z.boolean().optional(),
    organizerUsername: z.string().optional(),
    coverImageUrl: z.url().max(2000).optional(),
    videoUrl: z.url().max(2000).optional(),
    // Ссылка на карту (координаты) для Event.externalUrl — блок такси/маршрута.
    externalUrl: z.url().max(2000).optional(),
    placeId: z.uuid().nullable().optional(),
    categoryIds: z.array(z.uuid()).min(1).max(6),
    publish: z.boolean().default(true),
    force: z.boolean().default(false),
  })
  .refine(endsTogether, { message: endsTogetherMessage })
  .refine(
    (v) => !(v.startsAtLocalDates.length > 1 && (v.endsAtLocalDate != null || v.endsAtLocalTime != null)),
    {
      message: 'multi-date events cannot have an end date/time',
    },
  );
export type PublishFromImportInput = z.infer<typeof PublishFromImportInput>;

/** Ответ резолва Place из карт-ссылки поста (кнопка «создать место из карты»). */
export const ResolvedImportPlace = z.object({
  placeId: z.uuid(),
  venueText: z.string(),
});
export type ResolvedImportPlace = z.infer<typeof ResolvedImportPlace>;

/** Slug'и ленты для admin publish-as-feed (афиша — через PublishFromImportInput). */
export const PublishImportAsFeedCategorySlug = FeedContentCategorySlug.exclude(['events']);
export type PublishImportAsFeedCategorySlug = z.infer<typeof PublishImportAsFeedCategorySlug>;

export const PublishImportAsFeedInput = z.object({
  feedCategorySlug: PublishImportAsFeedCategorySlug,
  title: z.string().min(1).max(200).trim().optional(),
  body: z.string().max(5000).optional(),
});
export type PublishImportAsFeedInput = z.infer<typeof PublishImportAsFeedInput>;
